import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, FileCode2, Bell, FileText, Users, ClipboardList, 
  CreditCard, Calendar, MessageCircle, Database, Package, 
  Headphones, Mail, FileCheck, ArrowRight
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const iconMap = {
  Bell, FileText, Users, ClipboardList, CreditCard, Calendar,
  MessageCircle, Database, Package, Headphones, Mail, FileCheck
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`, { withCredentials: true });
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const handleUseTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/templates/${templateId}/use`, {}, { withCredentials: true });
      toast.success("Workflow created from template");
      navigate(`/workflows/${response.data.workflow_id}`);
    } catch (error) {
      toast.error("Failed to create workflow from template");
    }
  };

  const categories = ["All", ...new Set(templates.map(t => t.category))];

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === "All" || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Templates</h1>
          <p className="text-muted-foreground mt-1">Start with pre-built workflow templates</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="search-templates"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="rounded-full"
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="glass-card animate-pulse">
                <CardContent className="p-6">
                  <div className="h-40 bg-muted/50 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredTemplates.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => {
              const IconComponent = iconMap[template.icon] || FileCode2;
              
              return (
                <Card 
                  key={template.template_id}
                  className="glass-card hover:-translate-y-1 transition-transform duration-300 group"
                  data-testid={`template-card-${template.template_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {template.category}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      {template.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {template.nodes?.length || 0} nodes
                      </span>
                      <Button 
                        size="sm"
                        onClick={() => handleUseTemplate(template.template_id)}
                        className="rounded-full"
                        data-testid={`use-template-${template.template_id}`}
                      >
                        Use Template <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="py-16 text-center">
              <FileCode2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                No templates found
              </h3>
              <p className="text-muted-foreground">
                Try a different search term or category
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
